import React from 'react';
import autoBind from 'react-autobind';
import { Map, OrderedMap } from 'immutable';
import { Redirect } from 'react-router-dom';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import IconButton from 'material-ui/IconButton';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import AddImage from 'material-ui/svg-icons/editor/insert-photo';
import AddText from 'material-ui/svg-icons/editor/mode-edit';
import Drag from 'material-ui/svg-icons/editor/drag-handle';
import RotateIcon from 'material-ui/svg-icons/image/rotate-right';
import Editor from '../Editor';
import FieldImage from '../../../components/FieldImage';
import Infographic from '../../../components/Infographic';
import './style.scss';
import { InfographicRecord } from '../../../feathers/records';
import noty from '../../../lib/noty';
import { Panel } from '../../../components/Panel';
import colors from '../../../themes/colors';

function infographicMap(infographic) {
  return Map({
    touched: false,
    editing: false,
    fetching: false,
    infographic
  });
}

class InfographicEditor extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      open: true,
      redirect: null,
      // OrderedMap instead of Map to maintain drag-drop order in map form
      // Bc infographics are edited as a set, rather than individually,
      // create the OrderedMap with metadata to allow bulk editing.
      items: OrderedMap(),
      newInfographic: Map({
        touched: false,
        fetching: false,
        infographic: null
      })
    };
  }

  async componentDidMount() {
    const infographics = await this.props.marker.infographics();
    const items = OrderedMap(
      infographics.map(infographic => {
        return [infographic._id, infographicMap(infographic)];
      })
    );
    this.setState({ items });
  }

  addNewInfographic(type) {
    const infographic = new InfographicRecord({
      type,
      marker_id: this.props.marker._id
    });
    this.setState(
      {
        newInfographic: Map({
          touched: false,
          fetching: false,
          infographic
        })
      },
      () => {
        this.scrollContainer.scrollTop = this.scrollContainer.scrollHeight;
      }
    );
  }

  handleNewInfoGraphicCancel() {
    this.setState({
      newInfographic: Map({
        touched: false,
        fetching: false,
        infographic: null
      })
    });
  }

  handleNewInfographicChange({ name, value }) {
    const path = name.split('.');
    this.setState({
      newInfographic: Map({
        touched: true,
        fetching: false,
        infographic: this.state.newInfographic
          .get('infographic')
          .setIn(path, value)
      })
    });
  }

  handleNewInfographicSubmit() {
    const infographic = this.state.newInfographic.get('infographic');
    this.setState({
      newInfographic: this.state.newInfographic.set('fetching', true)
    });
    infographic
      .create()
      .then(created => {
        noty.success('Infographic created');
        this.setState({
          items: this.state.items.set(
            created._id,
            Map({
              touched: false,
              fetching: false,
              infographic: created
            })
          ),
          newInfographic: Map({
            touched: false,
            fetching: false,
            infographic: null
          })
        });
      })
      .catch(error => {
        this.setState({
          newInfographic: this.state.newInfographic.set('fetching', false)
        });
        noty.errors(error);
      });
  }

  handleEdit(id) {
    this.setState({
      items: this.state.items.setIn([id, 'editing'], true)
    });
  }

  handleChange(id, { name, value }) {
    const path = name.split('.');
    const infographic = this.state.items
      .getIn([id, 'infographic'])
      .setIn(path, value);
    const item = Map({
      touched: true,
      editing: true,
      fetching: false,
      infographic
    });
    this.setState({
      items: this.state.items.set(id, item)
    });
  }

  handleSubmit(id) {
    const infographic = this.state.items.getIn([id, 'infographic']);
    const item = Map({
      touched: true,
      editing: true,
      fetching: true,
      infographic
    });
    this.setState({
      items: this.state.items.set(id, item)
    });
    infographic
      .patch()
      .then(patched => {
        noty.success('Infographic updated');
        this.setState({
          items: this.state.items.set(
            id,
            Map({
              touched: false,
              editing: false,
              fetching: false,
              infographic: patched
            })
          )
        });
      })
      .catch(err => {
        this.setState({ fetching: false });
        noty.errors(err);
      });
  }

  handleRemove(id) {
    const infographic = this.state.items.getIn([id, 'infographic']);
    noty.confirm(`Are you sure that you want to delete this block?`, {
      onOk: () => {
        this.setState({
          items: this.state.items.set(
            id,
            Map({
              touched: true,
              editing: true,
              fetching: true,
              infographic: infographic
            })
          )
        });
        infographic
          .remove()
          .then(() => {
            noty.success('Infographic removed');
          })
          .catch(err => {
            this.setState({
              items: this.state.items.set(
                id,
                Map({
                  touched: true,
                  editing: true,
                  fetching: false,
                  infographic: infographic
                })
              )
            });
            noty.errors(err);
          });
      }
    });
  }

  handleClose() {
    const matchParams = this.props.match.params;
    const redirect = `/facilities/${matchParams.facilityId}/buildings/${
      matchParams.buildingId
    }/floors/${matchParams.floorId}/markers/${matchParams.markerId}`;
    const touched =
      this.state.items.reduce((touched, item) => {
        return touched || item.get('touched');
      }, false) || this.state.newInfographic.get('touched');
    if (touched) {
      noty.confirm(
        `Are you sure that you want to close the editor. You have unsaved changes.`,
        {
          onOk: () => {
            this.setState({ redirect });
          }
        }
      );
    } else {
      this.setState({ redirect });
    }
  }

  onDragEnd(result) {
    const items = this.state.items.toList();
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    // Place the new order directly in state to create fast experience
    // where the order happens quickly.
    const orderedItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );
    const orderedMap = OrderedMap(
      orderedItems.map(item => {
        return [item.getIn(['infographic', '_id']), item];
      })
    );
    this.setState({
      items: orderedMap
    });

    // Create the new array of ordered ids to return, which will
    // cause new props and a rerender to the current state.
    const newOrder = orderedMap.toArray().map(ig => ig[0]);
    const updatedMarker = this.props.marker.set('infographics_order', newOrder);
    updatedMarker.patch();
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }
    const { items, newInfographic: newInfographicItem, fetching } = this.state;
    const newInfographic = newInfographicItem.get('infographic');
    return (
      <div id="InfographicEditor">
        <div className="editor-modal">
          <div className="editor-modal-content animated fadeIn">
            <div className="display-container">
              <div className="display-window">
                <div
                  ref={ref => {
                    this.scrollContainer = ref;
                  }}
                  className="scroll-inner"
                >
                  <DragDropContext onDragEnd={this.onDragEnd}>
                    <Droppable droppableId="droppable">
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          style={getListStyle(snapshot.isDraggingOver)}
                        >
                          {items.toList().map((item, index) => {
                            const infographic = item.get('infographic');
                            const { _id } = infographic;
                            return (
                              <Draggable
                                key={_id}
                                draggableId={_id}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div>
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      // {...provided.dragHandleProps}
                                      style={getItemStyle(
                                        snapshot.isDragging,
                                        provided.draggableProps.style
                                      )}
                                    >
                                      <EditableInfographic
                                        dragHandleProps={
                                          provided.dragHandleProps
                                        }
                                        touched={item.get('touched')}
                                        editing={item.get('editing')}
                                        fetching={item.get('fetching')}
                                        infographic={infographic}
                                        onChange={change =>
                                          this.handleChange(_id, change)
                                        }
                                        onSubmit={() => this.handleSubmit(_id)}
                                        onDelete={() => this.handleRemove(_id)}
                                        onEdit={() => this.handleEdit(_id)}
                                      />
                                    </div>
                                    {makePlaceholder(provided.placeholder)}
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <div className="new-infographic">
                    {!newInfographic && (
                      <div className="animated fadeIn">
                        <AddButtons onAdd={this.addNewInfographic} />
                      </div>
                    )}
                    {newInfographic &&
                      newInfographic.type === 'content' && (
                        <div className="animated fadeIn">
                          <ContentEditor
                            infographic={newInfographic}
                            onChange={this.handleNewInfographicChange}
                            onSubmit={this.handleNewInfographicSubmit}
                            onDelete={this.handleNewInfoGraphicCancel}
                            fetching={newInfographicItem.get('fetching')}
                            saveTitle="Create Block"
                          />
                        </div>
                      )}
                    {newInfographic &&
                      newInfographic.type === 'image' && (
                        <div className="animated fadeIn">
                          <ImageEditor
                            infographic={newInfographic}
                            onChange={this.handleNewInfographicChange}
                            onSubmit={this.handleNewInfographicSubmit}
                            onDelete={this.handleNewInfoGraphicCancel}
                            fetching={newInfographicItem.get('fetching')}
                            saveTitle="Create Block"
                          />
                        </div>
                      )}
                  </div>
                  {!newInfographic &&
                    !fetching &&
                    items.size < 1 && (
                      <div style={{ padding: 32 }}>
                        <h2
                          style={{
                            margin: 0,
                            color: colors.primaryButtonColor,
                            paddingBottom: 16,
                            textAlign: 'center'
                          }}
                        >
                          Getting started with the Infographics Creator
                        </h2>
                        <div style={{ padding: 16 }}>
                          <p>
                            Infographics are step-by-step instructions for a
                            building asset. For example, it could be
                            instructions for turning off a gas line or a water
                            line, or setting the clock for your building’s
                            outdoor lights.
                          </p>
                          <br />
                          <p>
                            You build your infographic by adding blocks of
                            information. A block is either an image or a bit of
                            text. Add as many blocks as you need.
                          </p>
                          <br />
                          <p>
                            <a
                              href="https://mapplug.zendesk.com/hc/en-us/articles/360006404053-Adding-an-infographic-to-a-marker"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Visit this help page
                            </a>{' '}
                            to see examples and watch an explainer video.
                          </p>
                          <br />
                          <p>
                            To get started, click “Add Image” or “Add Text”
                            above.
                          </p>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
            <div className="display-footer">
              <FlatButton
                label="Close"
                style={{ marginLeft: 'auto' }}
                onClick={this.handleClose}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default InfographicEditor;

const AddButtons = ({ onAdd }) => (
  <div className="add-buttons-container">
    <div className="row around-xs center-xs">
      <div className="col-xs">
        <RaisedButton
          primary
          label="Add Image"
          icon={<AddImage />}
          onClick={() => onAdd('image')}
        />
      </div>
      <div className="col-xs">
        <RaisedButton
          primary
          label="Add Text"
          icon={<AddText />}
          onClick={() => onAdd('content')}
        />
      </div>
    </div>
  </div>
);

const ContentEditor = props => (
  <div style={{ padding: 32 }}>
    <Panel
      checkCanAdd="infographics"
      onSaveClick={props.onSubmit}
      onDeleteClick={props.onDelete}
      saveTitle={props.saveTitle}
      fetching={props.fetching}
      touched={props.touched}
      touchedMessage={props.touchedMessage}
    >
      <Editor
        name="content"
        value={props.infographic.content}
        onChange={props.onChange}
        label="Infographic Content"
        disabled={props.fetching}
      />
    </Panel>
  </div>
);

const ImageEditor = props => (
  <div style={{ padding: 32 }}>
    <Panel
      onSaveClick={props.onSubmit}
      onDeleteClick={props.onDelete}
      saveTitle={props.saveTitle}
      fetching={props.fetching}
      touched={props.touched}
      touchedMessage={props.touchedMessage}
    >
      <FieldImage
        value={props.infographic.image}
        name="image"
        onChange={props.onChange}
        label="Infographic Image"
        disabled={props.fetching}
      />
      <Infographic infographic={props.infographic} />
      <IconButton
        onClick={() => {
          props.onChange({
            name: 'image.heading',
            value:
              props.infographic.image.heading === 270
                ? 0
                : (props.infographic.image.heading || 0) + 90
          });
        }}
        tooltip="Rotate"
        tooltipPosition="top-right"
        disabled={props.fetching}
      >
        <RotateIcon color="#999" hoverColor="#000" />
      </IconButton>
    </Panel>
  </div>
);

class EditableInfographic extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
  }

  render() {
    const {
      dragHandleProps,
      infographic,
      editing,
      touched,
      fetching,
      onChange,
      onSubmit,
      onDelete,
      onEdit
    } = this.props;
    const isImage = infographic.type === 'image';
    return (
      <div className="editable-infographic">
        <div className="edit-bar">
          <FlatButton
            primary
            label={isImage ? 'Edit Image' : 'Edit Text'}
            icon={isImage ? <AddImage /> : <AddText />}
            onClick={onEdit}
            disabled={editing}
          />
          <div className="drag-handle" {...dragHandleProps}>
            <Drag />
            <div className="tool-tip">Drag to reorder</div>
          </div>
        </div>
        <div className="edit-container">
          {!editing && (
            <div>
              <Infographic infographic={infographic} />
            </div>
          )}
          {editing &&
            infographic.type === 'content' && (
              <div className="animated fadeIn">
                <ContentEditor
                  infographic={infographic}
                  onChange={onChange}
                  onSubmit={onSubmit}
                  onDelete={onDelete}
                  touched={touched}
                  saveTitle="Save Block"
                  fetching={fetching}
                />
              </div>
            )}
          {editing &&
            infographic.type === 'image' && (
              <div className="animated fadeIn">
                <ImageEditor
                  infographic={infographic}
                  onChange={onChange}
                  onSubmit={onSubmit}
                  onDelete={onDelete}
                  touched={touched}
                  saveTitle="Save Block"
                  fetching={fetching}
                />
              </div>
            )}
        </div>
      </div>
    );
  }
}

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  // styles we need to apply on draggables
  ...draggableStyle
});

const getListStyle = isDraggingOver => ({
  background: isDraggingOver ? '#ededed' : ''
});

// just using placeholder has problems getting margin right
// https://github.com/atlassian/react-beautiful-dnd/issues/153
const makePlaceholder = placeholder => {
  if (placeholder) {
    // meh...being immutable here is a PIA, just mutate it
    placeholder.props.placeholder.margin.bottom = 16;
  }
  return placeholder;
};



// WEBPACK FOOTER //
// ./src/pages/Facility/InfographicEditor/index.jsx